import React from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';

import { PageHeader, DataTableCard, EntityDrawer, ConfirmDialog } from '../ui';
import InventoryFormDrawer from './InventoryFormDrawer';
import buildInventoryColumns from './buildInventoryColumns';
import useInventoryPage from './useInventoryPage';

const InventoryPage = () => {
  const p = useInventoryPage();

  const columns = buildInventoryColumns({ t: p.t, openRowMenu: p.openRowMenu });

  return (
    <Box>
      <PageHeader
        title={p.t('Inventory')}
        subtitle={p.t('{{count}} products tracked', { count: p.total, defaultValue: `${p.total} products tracked` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search Products') }}
        helpKey="inventory"
        actions={[
          { label: p.t('Add stock', { defaultValue: 'Add stock' }), icon: AddIcon, variant: 'contained', onClick: p.openAddDrawer },
        ]}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        bulkBar={{
          count: p.selection.length,
          countLabel: p.t('{{count}} selected', { count: p.selection.length, defaultValue: `${p.selection.length} selected` }),
          actions: [
            { label: p.t('Delete'), onClick: () => p.openDeleteConfirm(p.selectedItems) },
          ],
          onClear: () => p.setSelection([]),
        }}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No products match "{{query}}"', { query: p.search, defaultValue: `No products match "${p.search}"` }),
          description: p.t('Try a different search.', { defaultValue: 'Try a different search.' }),
          actions: [
            { label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); } },
          ],
        } : {
          kind: 'none',
          icon: Inventory2Icon,
          title: p.t('No inventory yet', { defaultValue: 'No inventory yet' }),
          description: p.t('Add stock for a product to start tracking levels.', { defaultValue: 'Add stock for a product to start tracking levels.' }),
          actions: [{ label: p.t('Add stock', { defaultValue: 'Add stock' }), variant: 'contained', onClick: p.openAddDrawer }],
        }}
        error={{
          title: p.t("Couldn't load inventory", { defaultValue: "Couldn't load inventory" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: p.fetchInventory,
        }}
        permission={{
          title: p.t("You don't have access to Inventory", { defaultValue: "You don't have access to Inventory" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row.invertoryId}
        checkboxSelection
        rowSelectionModel={p.selection}
        onRowSelectionModelChange={p.setSelection}
        onRowClick={(params, event) => {
          if (event.target.closest('.MuiCheckbox-root') || event.target.closest('button')) return;
          p.openEditDrawer(params.row);
        }}
        sortingMode="server"
        sortModel={p.sortModel}
        onSortModelChange={p.setSortModel}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <Menu anchorEl={p.rowMenuAnchor} open={Boolean(p.rowMenuAnchor)} onClose={p.closeRowMenu}>
        <MenuItem onClick={() => { p.openEditDrawer(p.rowMenuItem); p.closeRowMenu(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{p.t('Edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { p.openDeleteConfirm([p.rowMenuItem]); p.closeRowMenu(); }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{p.t('Delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeDrawer}
        title={p.drawerMode === 'create' ? p.t('Add Inventory Item') : p.t('Edit Inventory Item')}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('Cancel')}
        primaryLabel={p.saving
          ? (p.drawerMode === 'create' ? p.t('Creating...') : p.t('Updating...'))
          : (p.drawerMode === 'create' ? p.t('Create') : p.t('Save'))}
        onPrimary={p.handleSave}
        primaryDisabled={p.saving || (p.drawerMode === 'create' && !p.productPickerId)}
        loading={p.saving}
      >
        <InventoryFormDrawer
          t={p.t}
          drawerMode={p.drawerMode}
          filteredProducts={p.filteredProducts}
          productPickerId={p.productPickerId}
          setProductPickerId={p.setProductPickerId}
          productSearch={p.productSearch}
          setProductSearch={p.setProductSearch}
          pickedProduct={p.pickedProduct}
          quantity={p.quantity}
          setQuantity={p.setQuantity}
          previousQuantity={p.previousQuantity}
          lowStockThreshold={p.lowStockThreshold}
          setLowStockThreshold={p.setLowStockThreshold}
          saving={p.saving}
        />
      </EntityDrawer>

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={p.confirmTargets.length === 1
          ? p.t('Delete "{{name}}"?', { name: p.confirmTargets[0]?.productName, defaultValue: `Delete "${p.confirmTargets[0]?.productName}"?` })
          : p.t('Delete {{count}} products?', { count: p.confirmTargets.length, defaultValue: `Delete ${p.confirmTargets.length} products?` })}
        description={p.t('This cannot be undone.', { defaultValue: 'This cannot be undone.' })}
        records={p.confirmTargets.map((item) => item.productName)}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Delete', { defaultValue: 'Delete' })}
        onConfirm={p.handleConfirmDelete}
        loading={p.deleting}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryPage;
